
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Divider,
    Alert,
    Switch
} from '@mui/material';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, verifyBeforeUpdateEmail, deleteUser } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FamilyManagement } from './FamilyManagement';
import { NotificationsActive, NotificationsOff } from '@mui/icons-material';

interface NotificationSettingsProps {
    enabled?: boolean;
    onToggle: (checked: boolean) => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ enabled, onToggle }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Notifications</Typography>
                <Switch
                    checked={enabled || false}
                    onChange={(e) => onToggle(e.target.checked)}
                    color="primary"
                />
            </Box>

            <Typography variant="caption" color="text.secondary">
                Recevez un email à chaque nouvel événement (sur {enabled ? 'votre adresse' : 'votre adresse'}).
            </Typography>
        </Box>
    );
};
// Attends, je ne peux pas changer la signature de NotificationSettings ici sans changer l'appel dans ProfileDialog.
// Je vais plutôt intégrer la logique directement dans ProfileDialog ou modifier NotificationSettings pour accepter des props.
// L'instruction est de remplacer le contenu.
// Je vais modifier NotificationSettings pour qu'il prenne 'userProfile' en props.


interface UserProfile {
    firstName: string;
    lastName: string;
    nickname: string;
    city: string;
    team?: string;
    notificationsEnabled?: boolean;
}

interface ProfileDialogProps {
    open: boolean;
    onClose: () => void;
}

export const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, onClose }) => {
    const { user } = useAuth();

    const [profile, setProfile] = useState<UserProfile>({
        firstName: '',
        lastName: '',
        nickname: '',
        city: '',
        team: '',
        notificationsEnabled: false
    });

    const [profileEmail, setProfileEmail] = useState<string>('');

    // ... (dialog states) ...

    const handleNotificationToggle = async (checked: boolean) => {
        setProfile(prev => ({ ...prev, notificationsEnabled: checked }));
        // Sauvegarde immédiate en base de données sans attendre le bouton "Enregistrer"
        if (user) {
            try {
                await setDoc(doc(db, 'users', user.uid), { notificationsEnabled: checked }, { merge: true });
            } catch (error) {
                console.error("Erreur sauvegarde notification:", error);
                // On pourrait rollback le state ici si erreur, mais c'est peu probable
            }
        }
    };


    // Dialogs sub-state (Email / Password change)
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [tmpEmail, setTmpEmail] = useState('');
    const [tmpEmailConfirm, setTmpEmailConfirm] = useState('');
    const [tmpCurrentPassword, setTmpCurrentPassword] = useState('');
    const [tmpPassword, setTmpPassword] = useState('');

    const [tmpPasswordConfirm, setTmpPasswordConfirm] = useState('');

    // Password for deletion confirmation
    const [deletePassword, setDeletePassword] = useState('');


    useEffect(() => {
        const loadProfile = async () => {
            if (user && open) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data() as UserProfile;
                    setProfile(data);
                    setProfileEmail(user.email || '');

                    // Si activé dans le profil, on tente d'enregistrer le token de cet appareil
                    /*
                    if (data.notificationsEnabled) {
                        // registerToken(); // REMOVED: Legacy Push Notifications
                    }
                    */
                }
            }
        };
        loadProfile();
    }, [user, open]);

    const handleProfileSave = async () => {
        if (!user) return;
        try {
            await setDoc(doc(db, 'users', user.uid), profile, { merge: true });
            onClose();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du profil:", error);
            alert("Erreur lors de la sauvegarde.");
        }
    };

    const handleChangeEmail = async () => {
        if (tmpEmail !== tmpEmailConfirm || !tmpEmail) { alert('Les emails ne correspondent pas.'); return; }
        const currentUser = auth.currentUser!;
        try {
            // réauthentifier
            const cred = EmailAuthProvider.credential(currentUser.email!, tmpCurrentPassword);
            await reauthenticateWithCredential(currentUser, cred);
            // Envoyer un lien de validation au NOUVEL email
            const actionCodeSettings = {
                url: window.location.origin,
                handleCodeInApp: true
            };
            await verifyBeforeUpdateEmail(currentUser, tmpEmail, actionCodeSettings);

            // Met à jour Firestore (on anticipe, mais idéalement il faudrait attendre la validation)
            await setDoc(doc(db, 'users', currentUser.uid), { email: tmpEmail }, { merge: true });

            setProfileEmail(tmpEmail);
            alert('Un email de confirmation a été envoyé à ' + tmpEmail + '. Cliquez sur le lien pour valider votre nouvelle adresse.');
            setOpenEmailDialog(false);
        } catch (err: any) {
            console.error('Erreur:', err);
            alert('Erreur: ' + err.message);
        }
    };

    const handleChangePassword = async () => {
        if (tmpPassword !== tmpPasswordConfirm || !tmpPassword) { alert('Les mots de passe ne correspondent pas.'); return; }
        try {
            await updatePassword(auth.currentUser!, tmpPassword);
            setOpenPasswordDialog(false);
            alert("Mot de passe mis à jour avec succès.");
        } catch (err: any) {
            alert('Erreur: ' + err.message);
        }
    };

    const handleDeleteAccount = async () => {
        if (!user) return;
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.email) return;

        if (!deletePassword) {
            alert("Veuillez entrer votre mot de passe pour confirmer.");
            return;
        }

        try {
            // 0. Ré-authentification
            const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
            await reauthenticateWithCredential(currentUser, credential);

            // 1. Anonymisation dans Firestore
            await setDoc(doc(db, 'users', user.uid), {
                email: "nous@quitté",
                firstName: "Parti",
                lastName: "PARTI",
                city: "",
                // On garde le nickname tel quel
                // On garde la team tel quel (pour stats)
            }, { merge: true });

            // 2. Suppression de l'authentification
            await deleteUser(currentUser);

            // Fermer les dialogues (l'utilisateur sera déconnecté automatiquement par le AuthContext)
            setOpenDeleteDialog(false);
            setDeletePassword('');
            onClose();

        } catch (error: any) {
            console.error("Erreur lors de la suppression du compte:", error);
            if (error.code === 'auth/wrong-password') {
                alert("Mot de passe incorrect.");
            } else if (error.code === 'auth/requires-recent-login') {
                // Should not happen if reauthenticate succeeds, but good to keep
                alert("Veuillez vous reconnecter.");
            } else {
                alert("Une erreur est survenue : " + error.message);
            }
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Mon profil</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Prénom"
                            value={profile.firstName}
                            onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Nom"
                            value={profile.lastName}
                            onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Surnom"
                            value={profile.nickname}
                            onChange={(e) => setProfile({ ...profile, nickname: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Ville"
                            value={profile.city}
                            onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Team"
                            value={profile.team}
                            onChange={(e) => setProfile({ ...profile, team: e.target.value })}
                            fullWidth
                            helperText="Optionnel - Le nom de votre équipe"
                        />

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            <Typography sx={{ minWidth: 100, fontWeight: { xs: 'bold', sm: 'normal' } }}>Email</Typography>
                            <Typography sx={{ wordBreak: 'break-all' }}>{profileEmail}</Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setOpenEmailDialog(true)}
                                sx={{ mt: { xs: 1, sm: 0 } }}
                            >
                                Changer
                            </Button>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            <Typography sx={{ minWidth: 100, fontWeight: { xs: 'bold', sm: 'normal' } }}>Mot de passe</Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setOpenPasswordDialog(true)}
                                sx={{ mt: { xs: 1, sm: 0 } }}
                            >
                                Changer
                            </Button>
                        </Box>



                        <Divider sx={{ my: 2 }} />

                        {/* Notifications */}
                        <NotificationSettings
                            enabled={profile.notificationsEnabled}
                            onToggle={handleNotificationToggle}
                        />

                        {/* Intégration du composant Famille */}
                        <FamilyManagement userProfile={profile} />

                        <Divider sx={{ my: 2 }} />

                        {/* Zone de danger */}
                        <Box sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'error.light',
                            borderRadius: 1,
                            bgcolor: 'rgba(211, 47, 47, 0.05)' // Transparent red
                        }}>
                            <Typography variant="body2" color="text.primary" paragraph sx={{ textAlign: 'center' }}>
                                La suppression de votre compte est irréversible.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setOpenDeleteDialog(true)}
                                >
                                    Supprimer mon compte
                                </Button>
                            </Box>
                        </Box>

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Annuler</Button>
                    <Button onClick={handleProfileSave} variant="contained">Enregistrer</Button>
                </DialogActions>
            </Dialog>

            {/* Sous-dialogue : Changer Email */}
            <Dialog open={openEmailDialog} onClose={() => setOpenEmailDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle>Changer mon email</DialogTitle>
                <DialogContent>
                    <TextField label="Nouvel email" value={tmpEmail} onChange={(e) => setTmpEmail(e.target.value)} fullWidth margin="dense" />
                    <TextField label="Confirmer email" value={tmpEmailConfirm} onChange={(e) => setTmpEmailConfirm(e.target.value)} fullWidth margin="dense" />
                    <TextField label="Mot de passe actuel" type="password" value={tmpCurrentPassword} onChange={(e) => setTmpCurrentPassword(e.target.value)} fullWidth margin="dense" helperText="Requis pour confirmer ce changement" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEmailDialog(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleChangeEmail}>Confirmer</Button>
                </DialogActions>
            </Dialog>

            {/* Sous-dialogue : Changer Mot de passe */}
            <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle>Changer mon mot de passe</DialogTitle>
                <DialogContent>
                    <TextField label="Nouveau mot de passe" type="password" value={tmpPassword} onChange={(e) => setTmpPassword(e.target.value)} fullWidth margin="dense" />
                    <TextField label="Confirmer mot de passe" type="password" value={tmpPasswordConfirm} onChange={(e) => setTmpPasswordConfirm(e.target.value)} fullWidth margin="dense" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPasswordDialog(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleChangePassword}>Confirmer</Button>
                </DialogActions>
            </Dialog>

            {/* Sous-dialogue : Confirmation Suppression */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ color: 'error.main' }}>Supprimer le compte ?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Attention, cette action est irréversible.
                    </Alert>
                    <Typography variant="body1">
                        Êtes-vous sûr de vouloir nous quitter ? <br />
                        Votre compte sera supprimé définitivement.
                    </Typography>
                    <TextField
                        label="Confirmez votre mot de passe"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        helperText="Requis pour supprimer le compte"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteAccount}>
                        Oui, supprimer mon compte
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
